import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const categoryOptions = [
  { value: "Interpretation", label: "Service: Interpretation" },
  { value: "TranslationOther", label: "Service: Translation / Other" },
  { value: "Translation", label: "Service: Translation" },
  { value: "Other", label: "Service: Other" },
];

const NotificationNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { overlayData, setOverlayName, showSnackbar, handleErrorDialog, openDialog, closeDialog } = useStateContext();

  const rule = overlayData?.rule || null;
  const isEditMode = Boolean(rule?.id);
  const isSystemDefaultRule = Boolean(rule?.IsSystemDefault);
  const options = useMemo(() => overlayData?.ruleOptions || {}, [overlayData?.ruleOptions]);

  const [name, setName] = useState(rule?.Name || "");
  const [serviceCategory, setServiceCategory] = useState(rule?.ServiceCategory || "Interpretation");
  const [triggerType, setTriggerType] = useState(rule?.TriggerType || "interpretation_start_in_minutes");
  const [triggerValue, setTriggerValue] = useState(
    rule?.TriggerValue === null || rule?.TriggerValue === undefined ? "" : String(rule.TriggerValue)
  );
  const [repeatUnreadMinutes, setRepeatUnreadMinutes] = useState(
    rule?.RepeatUnreadMinutes === null || rule?.RepeatUnreadMinutes === undefined
      ? ""
      : String(rule.RepeatUnreadMinutes)
  );
  const [isEnabled, setIsEnabled] = useState(rule?.IsEnabled !== false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const triggerOptions = useMemo(() => {
    if (serviceCategory === "Interpretation") {
      return [
        {
          value: "interpretation_start_in_minutes",
          label: "TimeStart is in X minutes",
          values: options.interpretationStartMinutes || [15, 30, 45, 60],
        },
        {
          value: "interpretation_end_hours_ago",
          label: "TimeEnd was X hours ago",
          values: options.interpretationEndHoursAgo || [6, 12, 18, 24, 36, 48],
        },
      ];
    }

    return [
      {
        value: "service_deadline_in_days",
        label: "Deadline is in X days",
        values: options.deadlineDaysIn || [3, 2, 1],
      },
      {
        value: "service_deadline_today",
        label: "Deadline is today",
        values: [],
      },
      {
        value: "service_deadline_days_ago",
        label: "Deadline was X days ago",
        values: options.deadlineDaysAgo || [1, 2, 3, 5, 7],
      },
    ];
  }, [serviceCategory, options]);

  const activeTriggerOption = useMemo(
    () => triggerOptions.find((item) => item.value === triggerType) || triggerOptions[0],
    [triggerOptions, triggerType]
  );

  const requiresTriggerValue = (activeTriggerOption?.values || []).length > 0;

  const normalizePayload = () => ({
    Name: name.trim(),
    ServiceCategory: serviceCategory,
    TriggerType: triggerType,
    TriggerValue: requiresTriggerValue && triggerValue !== "" ? Number(triggerValue) : null,
    RepeatUnreadMinutes: repeatUnreadMinutes === "" ? null : Number(repeatUnreadMinutes),
    IsSystemDefault: isSystemDefaultRule,
    IsEnabled: Boolean(isEnabled),
  });

  const emitSettingsSaved = (recordId, changeAction) => {
    if (!socket || !currentUser?.id) return;

    socket.emit("settings:saved", {
      settingsType: "NotificationRule",
      recordId,
      changeAction,
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showSnackbar("Rule name is required", theme, "error");
      return;
    }

    if (requiresTriggerValue && triggerValue === "") {
      showSnackbar("Select a trigger value", theme, "error");
      return;
    }

    const payload = normalizePayload();
    setSaving(true);

    try {
      if (isEditMode) {
        await api.put(`/notificationSettings/member/rules/${rule.id}`, payload);
        emitSettingsSaved(rule.id, "Updated");
        showSnackbar("Notification rule updated", theme, "success");
      } else {
        const response = await api.post("/notificationSettings/member/rules", payload);
        emitSettingsSaved(response.data?.id, "Created");
        showSnackbar("Notification rule created", theme, "success");
      }

      overlayData?.onSaved?.();
      closeOverlay();
    } catch (error) {
      handleErrorDialog(error?.response?.data?.error || "Error saving notification rule", theme);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEditMode) return;

    openDialog({
      theme,
      title: "Delete Notification Rule",
      message: `Delete "${name}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);
        try {
          await api.delete(`/notificationSettings/member/rules/${rule.id}`);
          closeDialog();
          emitSettingsSaved(rule.id, "Deleted");
          showSnackbar("Notification rule deleted", theme, "success");
          overlayData?.onSaved?.();
          closeOverlay();
        } catch (error) {
          closeDialog();
          handleErrorDialog(error?.response?.data?.error || "Error deleting notification rule", theme);
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const pageTitle = isEditMode
    ? isSystemDefaultRule
      ? "Edit System Default Rule"
      : "Edit Notification Rule"
    : isSystemDefaultRule
      ? "New System Default Rule"
      : "New Notification Rule";

  const getFieldSx = (hasValue) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent !important",
      "& fieldset": {
        borderColor: hasValue ? theme.palette.primary.main : theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: hasValue ? theme.palette.primary.main : theme.palette.text.secondary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused": {
        backgroundColor: "transparent !important",
      },
      "& .MuiSelect-select": {
        backgroundColor: "transparent !important",
      },
      "& input": {
        backgroundColor: "transparent !important",
      },
    },
    "& .MuiInputBase-root": {
      backgroundColor: "transparent !important",
    },
  });

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isEditMode && (
          isMobile ? (
            <IconButton
              color="error"
              onClick={handleDelete}
              disabled={deleting}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.error.main}`,
                borderRadius: "12px",
              }}
            >
              <DeleteRoundedIcon />
            </IconButton>
          ) : (
            <Button
              color="error"
              variant="outlined"
              size="large"
              endIcon={<DeleteRoundedIcon />}
              onClick={handleDelete}
              disabled={deleting}
              sx={{ borderRadius: "12px" }}
            >
              Delete
            </Button>
          )
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <IconButton
            color="primary"
            onClick={closeOverlay}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        {isMobile ? (
          <IconButton
            color="primary"
            onClick={handleSave}
            disabled={saving}
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.default,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            <SaveRoundedIcon />
          </IconButton>
        ) : (
          <Button
            color="primary"
            variant="contained"
            size="large"
            endIcon={<SaveRoundedIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: "12px" }}
          >
            {saving ? "Saving..." : isEditMode ? "Update" : "Create"}
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Overlay
      id="notification-new-edit"
      open={true}
      onClose={closeOverlay}
      title={pageTitle}
      height="fit-content"
      footer={footerActions}
    >
      <Box
        sx={{
          flexGrow: 1,
          p: "1rem 2.3rem 1rem 2.5rem",
          height: "100%",
          width: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
          <TextField
            size="small"
            label="Rule Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            sx={getFieldSx(Boolean(name?.trim()))}
          />

          <FormControl size="small" fullWidth sx={getFieldSx(Boolean(serviceCategory))}>
            <InputLabel id="service-category-label">Service Scope</InputLabel>
            <Select
              size="small"
              labelId="service-category-label"
              value={serviceCategory}
              label="Service Scope"
              onChange={(event) => {
                const nextCategory = event.target.value;
                setServiceCategory(nextCategory);
                const nextOptions = nextCategory === "Interpretation"
                  ? ["interpretation_start_in_minutes", "interpretation_end_hours_ago"]
                  : ["service_deadline_in_days", "service_deadline_today", "service_deadline_days_ago"];
                setTriggerType(nextOptions[0]);
                setTriggerValue("");
              }}
            >
              {categoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth sx={getFieldSx(Boolean(triggerType))}>
            <InputLabel id="trigger-type-label">Trigger</InputLabel>
            <Select
              size="small"
              labelId="trigger-type-label"
              value={triggerType}
              label="Trigger"
              onChange={(event) => {
                setTriggerType(event.target.value);
                setTriggerValue("");
              }}
            >
              {triggerOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {requiresTriggerValue && (
            <FormControl size="small" fullWidth sx={getFieldSx(Boolean(triggerValue))}>
              <InputLabel id="trigger-value-label">X Value</InputLabel>
              <Select
                size="small"
                labelId="trigger-value-label"
                value={triggerValue}
                label="X Value"
                onChange={(event) => setTriggerValue(event.target.value)}
              >
                {activeTriggerOption.values.map((value) => (
                  <MenuItem key={value} value={String(value)}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" fullWidth sx={getFieldSx(Boolean(repeatUnreadMinutes))}>
            <InputLabel id="repeat-unread-label">Re-ring if unread (minutes)</InputLabel>
            <Select
              size="small"
              labelId="repeat-unread-label"
              value={repeatUnreadMinutes}
              label="Re-ring if unread (minutes)"
              onChange={(event) => setRepeatUnreadMinutes(event.target.value)}
            >
              <MenuItem value="">No re-ring</MenuItem>
              {(options.repeatUnreadMinutes || [5, 10, 15, 30, 60]).map((value) => (
                <MenuItem key={value} value={String(value)}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5 }}>
            <Typography variant="body1">Rule Enabled</Typography>
            <Switch checked={isEnabled} onChange={(event) => setIsEnabled(event.target.checked)} />
          </Box>
        </Box>
    </Overlay>
  );
};

export default NotificationNewEdit;